import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { z } from "zod";
import { PhoneInput } from "@/components/ui/phone-input";

const memberSchema = z.object({
  full_name: z.string().min(1, "Full name is required").max(100),
  address: z.string().max(500).optional(),
  phone: z.string().max(20).optional(),
  email: z.string().email("Invalid email address").max(255),
  number_of_kids: z.number().min(0).max(50),
});

const MemberRegistration = () => {
  const { user, isAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingMembership, setCheckingMembership] = useState(true);
  const [formData, setFormData] = useState({
    full_name: "",
    address: "",
    phone: "",
    email: user?.email || "",
    number_of_kids: 0,
  });

  useEffect(() => {
    const checkExistingMembership = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("members")
          .select("id, membership_status")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          toast({
            title: t("member.registration.alreadymember"),
            description: t("member.registration.alreadymessage").replace("{status}", data.membership_status),
          });
          navigate("/member-portal");
        }
      } catch (error: any) {
        console.error("Error checking membership:", error);
      } finally {
        setCheckingMembership(false);
      }
    };

    checkExistingMembership();
  }, [user, navigate, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Validate form
      const validated = memberSchema.parse(formData);

      // Admins get auto-approved, others need approval
      const membershipStatus = isAdmin ? "active" : "pending";

      // Insert member record - role will be auto-assigned by database trigger
      const { error } = await supabase.from("members").insert({
        user_id: user?.id!,
        full_name: validated.full_name,
        email: validated.email,
        address: validated.address || null,
        phone: validated.phone || null,
        number_of_kids: validated.number_of_kids,
        membership_status: membershipStatus,
      });

      if (error) throw error;

      toast({
        title: t("member.registration.success"),
        description: isAdmin 
          ? t("member.registration.successmessage.admin")
          : t("member.registration.successmessage"),
      });

      navigate("/member-portal");
    } catch (error: any) {
      console.error("Error submitting registration:", error);
      toast({
        title: t("member.registration.error"),
        description: error.message || t("member.registration.errormessage"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  if (checkingMembership) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container mx-auto py-8 px-4 max-w-2xl">
          <Card>
            <CardContent className="py-8 text-center">
              <p>{t("member.registration.checking")}</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto py-8 px-4 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>{t("member.registration.title")}</CardTitle>
            <CardDescription>
              {t("member.registration.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">{t("member.registration.fullname")} *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  required
                  maxLength={100}
                />
              </div>

              <div>
                <Label htmlFor="email">{t("member.registration.email")} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  maxLength={255}
                />
              </div>

              <div>
                <Label htmlFor="phone">{t("member.registration.phone")}</Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  onChange={(value) =>
                    setFormData({ ...formData, phone: value })
                  }
                />
              </div>

              <div>
                <Label htmlFor="address">{t("member.registration.address")}</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  maxLength={500}
                />
              </div>

              <div>
                <Label htmlFor="number_of_kids">{t("member.registration.children")}</Label>
                <Input
                  id="number_of_kids"
                  type="number"
                  min="0"
                  max="50"
                  value={formData.number_of_kids}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      number_of_kids: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("member.registration.submitting") : t("member.registration.submit")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default MemberRegistration;
